import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Moment from 'moment';
import { toast } from 'react-toastify';
import {
    get as _get,
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Input from '@mui/material/Input';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import InfoIcon from '@mui/icons-material/Info';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { LSThemeChanger } from './app';
import SearchBar from '../../components/searchbar';
import Validator, { useValidate, checkValidity } from '../../components/validator';
import ConfirmationDialog from '../../components/confirmationdlg';

import LSRestedService, { userFields } from '../../data/lsrested-service';

import Helpers, { TranslateHome, TranslateTheme } from '../../libraries/helpers';
import useWindowSize from '../../libraries/usewindowsize';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    roleUsers: {
        display: 'inline',
    },
    fullWidth: {
        width: '100%',
    },
    tabBar: {
        marginTop: theme.spacing(3),
    },
}));

const Users = () => {
    const classes = useStyles();
    const theme = useTheme();
    const [user, setUser] = useState({});
    const navigate = useNavigate();
    const [userData, setUserData] = useState([]);
    const [usersCanDelete, setUsersCanDelete] = useState({});
    const [search, setSearch] = useState('');
    const [searchData, setSearchData] = useState(null);
    const [deleteUserDialog, setDeleteUserDialog] = useState(false);
    const [passwordResetDialog, setPasswordResetDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const { height } = useWindowSize();
    const userTableAPIRef = useApiRef();
    const [userColumnWidth, setUserColumnWidth] = useState({});
    const [userSortModel, setUserSortModel] = useState([{
        field: 'data.title',
        sort: 'asc',
    }]);
    const [txfDashboards, setTxfDashboards] = useState(true);

    useEffect(() => {
        const u = LSRestedService.getAuth()?.user;
        setUser(u || null);
        setUsersCanDelete(u.username === 'admin' && u.permissions.includes('admin'))
        getUsers();
    }, []);

    const getUsers = () => {
        LSRestedService.getUsers().then(res => {
            const u = res.objects.map((v) => ({ ...v, id: Helpers.UniqueID() }));
            setUserData(u);
        }, err => {
            console.error('Unable to get users ::: ', err);
        });
    };

    const handleCreateUser = () => {
        navigate('/users/create_user', { state: { fromUsers: true } });
    };

    const handleUserSearch = ({ search }) => {
        setSearch(search);
    };
    
    useEffect(() => {
        runSearch(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const runSearch = (search) => {
        const SEARCH_FIELDS = ['username', 'name', 'email'];
        if (!search) {
            setSearchData(null);
            return;
        }

        setSearchData(userData.filter(v => {
            for (let x in v) {
                if (SEARCH_FIELDS.includes(x) && Object.prototype.hasOwnProperty.call(v, x)) {
                    if (String(v[x]).includes(search)) {
                        return v;
                    }
                }
            }
            return null;
        }).filter(x => x));
    };

    const tableRenderers = {
        name: d => (<div>{d.row.name}</div>),
        username: d => (<div>{d.row.username}</div>),
        email: d => (<div>{d.row.email}</div>),
        admin: d => (<div>{d.row.permissions.includes('admin') ? 'Yes': 'No'}</div>),
    };

    const userColumns = userFields.map(f => {
        return {
            ...f,
            width: userColumnWidth[f.headerName] || 0,
            ...(!userColumnWidth[f.headerName] ? { flex: 1 } : {}),
            disableColumnMenu: true,
            field: f.variable,
            renderCell: tableRenderers[f.variable],
        };
    });

    const handleSelectUser = (s) => {
        setSelectedUser(s.row);
    };

    const handleUserSortModelChange= (sortModel)=>{
        setUserSortModel(sortModel)
    }

    const handleEditUser = () => {
        navigate(`/users/update_user/${selectedUser.username}`, { state: { fromUsers: true } });
    };

    const handleCloseDeleteUserDialog = () => {
        setDeleteUserDialog(false);
        setTimeout(() => {
            setSelectedUser(null);
        }, 1000);
    };

    const handleOpenDeleteUser = () => {
        setDeleteUserDialog(true);
    };

    const handleDeleteUser = (e) => {
        LSRestedService.deleteUser(selectedUser.username).then(() => {
            getUsers();
            toast.success('User deleted successfully');
        }, err => {
            console.error('Error deleting user ::: ', err);
            toast.error('Error deleting user');
        }).finally(() => {
            setSelectedUser(null);
            setDeleteUserDialog(false);
        });
    };

    const handleClosePasswordResetDialog = () => {
        setPasswordResetDialog(false);
    };

    const handlePasswordReset = (e) => {
        toast.info('Sending reset password email to user...');
        LSRestedService.resetPassword(selectedUser.username);
        setPasswordResetDialog(false);
    };

    // const handleOpenResetPassword = () => {
    //     setPasswordResetDialog(true);
    // };

    const handleSetPassword = (e) => {
        navigate(`/users/password/${selectedUser.username}`);
    };

    return (
        <Box mt={3}>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <SearchBar
                        immediate
                        onSearch={handleUserSearch}
                        searchservice={{ searchPlaceholder: "Search users..." }}
                    />
                </Grid>
                <Grid item xs={6} display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        className={classes.buttonRight}
                        color="primary"
                        aria-label="add user"
                        onClick={handleCreateUser}
                    >
                        <AddIcon /> Create User
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    <Box height={height - 310} width="100%">
                        <DataGridPro
                            apiRef={userTableAPIRef}
                            components={{
                                Header: () => {
                                    return (
                                        <Box p={1} display="flex" justifyContent="flex-end" borderBottom="1px solid #666666">
                                            <IconButton
                                                disabled={!selectedUser || (selectedUser?.username === 'admin' && user.username !== 'admin')}
                                                variant="contained"
                                                name="set password"
                                                aria-label="set password"
                                                title="set password"
                                                onClick={handleSetPassword}
                                                size="large"><LockIcon /></IconButton>
                                            <IconButton
                                                disabled={!selectedUser}
                                                variant="contained"
                                                color="primary"
                                                name="edit user"
                                                aria-label="edit user"
                                                title="edit user"
                                                onClick={handleEditUser}
                                                size="large"><EditIcon /></IconButton>
                                            <IconButton
                                                disabled={!selectedUser ||
                                                selectedUser.username === user ||
                                                selectedUser.username === 'admin' ||
                                                !usersCanDelete
                                                }
                                                variant="contained"
                                                color="secondary"
                                                name="delete user"
                                                title="delete user"
                                                aria-label="delete user"
                                                onClick={handleOpenDeleteUser}
                                                size="large">
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    );
                                }
                            }}
                            rows={searchData || userData}
                            columns={userColumns}
                            onRowClick={handleSelectUser}
                            disableMultipleSelection
                            disableMultipleColumnsFiltering
                            disableMultipleColumnsSorting
                            disableColumnMenu
                            disableVirtualization
                            sortingOrder={['desc', 'asc']}
                            sortModel={userSortModel}
                            onSortModelChange={handleUserSortModelChange}
                        />
                    </Box>
                </Grid>
            </Grid>
            <ConfirmationDialog
                onClose={handleCloseDeleteUserDialog}
                onOK={handleDeleteUser}
                open={deleteUserDialog}
                message={
                    <div>
                        Are you sure you want to remove the user {selectedUser ? selectedUser.name : ''}?
                    </div>
                }
                messagee={`Are you sure you want to remove the user ${selectedUser ? selectedUser.name : ''}?`}
                title="Delete User"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleClosePasswordResetDialog}
                onOK={handlePasswordReset}
                open={passwordResetDialog}
                message={
                    `User ${selectedUser ? selectedUser.username : ''} (${selectedUser ? selectedUser.name : ''})
                    will receive an email with instructions to complete the password reset process.
                    Email will be sent to ${selectedUser ? selectedUser.email : ''}`
                }
                title="Send Reset Email"
                cancelLabel="Cancel"
                okLabel="Send Reset Email"
            />
        </Box>
    );
};

export default Users;
